/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Cell, Pie, PieChart, Tooltip, TooltipProps } from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/navigate-arrow.svg';
import { ReactComponent as RefreshIcon } from '../../../../assets/icons/dashboard/rotate-left.svg';
import { ReactComponent as LoaderIcon } from '../../../../assets/icons/loader.svg';
import { branding, connectorStatus } from '../../../../config/enum';
import { useAppDispatch } from '../../../../redux/hooks';
import { updateChargingPointFilters } from '../../../../redux/slices/DeviceManagementSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { updateChargingPointFilters as updatePersonalChargingPointFilters } from '../../../../redux/slices/PersonalDeviceSlice';
import MyButton from '../../../../shared-components/button/Button';
// import ToggleWithIcons from '../../../shared-components/toggle-with-icons/ToggleWithIcons';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import { getRelativeTime } from '../../../../shared-functions/DateTime';
import dashboard from '../../../../utils/api/dashboard';

type PieChartDataType = {
  name: string;
  stableKey: string;
  value: number;
  color: string;
};

interface QueryParameters {
  [key: string]: any;
}

interface ConnectorStatusChartProperties {
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * Connector Status
 * @param {any} param0 - The param0
 * @param {any} param0.filters - The filters prop
 * @returns {any} The result
 */
export default function ConnectorStatusChart({ filters }: ConnectorStatusChartProperties) {
  const { t } = useTranslation('dashboard');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [pieChartData, setPieChartData] = useState<PieChartDataType[]>([
    {
      name: t(connectorStatus.CHARGING),
      stableKey: connectorStatus.CHARGING,
      value: 0,
      color: 'var(--charging-color)',
    },
    {
      name: t(connectorStatus.AVAILABLE),
      stableKey: connectorStatus.AVAILABLE,
      value: 0,
      color: 'var(--available-color)',
    },
    {
      name: t(connectorStatus.PREPARING),
      stableKey: connectorStatus.PREPARING,
      value: 0,
      color: 'var(--preparing-color)',
    },
    {
      name: t(connectorStatus.UNAVAILABLE),
      stableKey: connectorStatus.UNAVAILABLE,
      value: 0,
      color: 'var(--unavailable-color)',
    },
    {
      name: t(connectorStatus.FINISHING),
      stableKey: connectorStatus.FINISHING,
      value: 0,
      color: 'var(--finishing-color)',
    },
    {
      name: t(connectorStatus.FAULTED),
      stableKey: connectorStatus.FAULTED,
      value: 0,
      color: 'var(--faulted-color)',
    },
    {
      name: t(connectorStatus.SUSPENDED_EV),
      stableKey: connectorStatus.SUSPENDED_EV,
      value: 0,
      color: 'var(--suspendedEV-color)',
    },
    {
      name: t(connectorStatus.SUSPENDED_EVSE),
      stableKey: connectorStatus.SUSPENDED_EVSE,
      value: 0,
      color: 'var(--suspendedEVSE-color)',
    },
    { name: t('Others'), stableKey: 'Others', value: 0, color: 'var(--unknown-color)' },
  ]);

  /**
   * Function to handle redirect device management page with filter
   * @param {any} key - The key
   */
  const handleClickPieChartValue = (key: string) => {
    let valueForFilter: string[] = [key];
    if (key === 'Others') {
      valueForFilter = [
        connectorStatus.PREPARING,
        connectorStatus.RESERVED,
        connectorStatus.UNAVAILABLE,
      ];
    }
    dispatch(
      updateChargingPointFilters({
        key: 'connectorStatus',
        value: valueForFilter,
        type: 'restAndAdd',
      }),
    );
    dispatch(
      updatePersonalChargingPointFilters({
        key: 'connectorStatus',
        value: valueForFilter,
        type: 'restAndAdd',
      }),
    );
    navigate({
      pathname: '/ev-admin/operations/chargers',
      search: `?${new URLSearchParams({
        // chargerType: activeChargerType,
      })}`,
    });
  };

  /**
   * Function to fetch connector status report
   * @param {any} query - The query
   */
  const fetchConnectorStatusReport = (query: QueryParameters) => {
    setIsLoading(true);
    const { facilityIds, stationIds, deviceIds } = filters || {};
    dashboard
      .getConnectorStatusReport({
        ...query,
        accountCustomId: facilityIds,
        stationIds,
        deviceIds,
        type: filters?.type,
      })
      .then((response) => {
        const result = Array.isArray(response.data?.finalResult) ? response.data.finalResult : [];
        setIsLoading(false);
        setPieChartData((previousData: any) =>
          previousData.map((item: any) => {
            const found = result.find((r: any) => r.status === item.stableKey);
            return {
              ...item,
              name: item.stableKey === 'Others' ? t('Others') : t(item.stableKey),
              value: found ? found.count : 0,
            };
          }),
        );
        if (response.data?.last_updated_time) {
          setLastUpdatedTime(response.data.last_updated_time);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to show custom tooltip
   * @param {any} param0 - The param0
   * @param {any} param0.active - The active prop
   * @param {any} param0.payload - The payload prop
   * @returns {any} The result
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0];

      return (
        <div className="connector-status-custom-tooltip">
          <p
            className="label"
            style={{ color: data?.payload?.color }}>{`${data.name} : ${data.value}`}</p>
        </div>
      );
    }

    return null;
  }

  useEffect(() => {
    fetchConnectorStatusReport({ hardRefresh: true });
  }, [filters]);

  return (
    <div className="connector-status-chart-wrap">
      <div className="connector-status-chart-header">
        <div className="header-title">
          <div className="title-wrapper">
            <h6>{t('connectorStatus')}</h6>
            <TooltipIcon text="Connector status chart" infoColor="info" />
          </div>
          <NavigateArrow
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/ev-admin/operations/chargers')}
          />
        </div>
      </div>
      <div className="connector-status-chart-container">
        <PieChart width={250} height={250}>
          <Pie
            data={pieChartData}
            cx={125}
            cy={125}
            innerRadius={60}
            outerRadius={100}
            fill="#8884d8"
            paddingAngle={2}
            dataKey="value">
            {pieChartData.map((entry) => (
              <Cell
                key={entry.stableKey}
                fill={entry.color}
                style={{ outline: 'none', cursor: 'pointer' }}
                onClick={() => handleClickPieChartValue(entry.stableKey)}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
        <div className="total-sessions-wrap">
          <p>
            {t('total')} <br /> {t('connectors')}
          </p>
          <span>{pieChartData.reduce((sum, item) => sum + item.value, 0)}</span>
        </div>
      </div>
      <div className="pie-chart-label">
        {pieChartData.map((eachData) => (
          <div className="each-label">
            <div
              className="each-label-color"
              style={{ '--label-color': eachData.color } as React.CSSProperties}
            />
            <p
              role="presentation"
              onClick={
                process.env.REACT_APP_BRANDING === branding.VOLTIE
                  ? () => handleClickPieChartValue(eachData.stableKey)
                  : undefined
              }
              style={
                process.env.REACT_APP_BRANDING === branding.VOLTIE
                  ? { cursor: 'pointer' }
                  : undefined
              }>
              {eachData.name}
            </p>
            <div className="each-label-value">
              <span>{eachData.value}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="refresh">
        <div className="refresh-icon">
          {isLoading ? (
            <div className="loading">
              <LoaderIcon style={{ width: '20px', height: '20px' }} />
            </div>
          ) : (
            <MyButton
              label=""
              buttonType="none"
              onClickFunc={() => fetchConnectorStatusReport({ hardRefresh: true })}
              svgBackIcon={<RefreshIcon style={{ width: '20px', height: '20px' }} />}
            />
          )}
        </div>
        <p className="updated-time">
          {t('lastUpdatedTime')} : {lastUpdatedTime ? getRelativeTime(lastUpdatedTime, t) : ''}{' '}
        </p>
      </div>
      {/* <div className="charger-status-toggle">
        <ToggleWithIcons
          leftMenu={chartToggle[0]}
          rightMenu={chartToggle[1]}
          handleClickToggle={handleToggleSwitch}
        />
      </div> */}
    </div>
  );
}
ConnectorStatusChart.defaultProps = {
  filters: undefined,
};
